import React, { useState } from 'react';
import { useStore, Product, isStashSeller } from '../../store/useStore';
import { Search, Bell, Heart, User, ShoppingCart, ChevronDown, Star, CheckCircle2, AlertCircle, CircleDashed, Tag, ShieldCheck } from 'lucide-react';
import { clsx } from 'clsx';
import { WmLogo } from './WmLogo';
const STATUS_CONFIG: Record<Product['status'], {
  label: string;
  Icon: typeof CheckCircle2;
  pill: string;
  description: string;
}> = {
  Stashed: {
    label: 'Stashed',
    Icon: CheckCircle2,
    pill: 'bg-[#E9FAF8] text-[#007E7A]',
    description: 'In stock, price locked, and recently verified at pickup. Backed by the Stash guarantee — if it’s wrong at pickup, we make it right.'
  },
  'Not verified': {
    label: 'Not verified',
    Icon: AlertCircle,
    pill: 'bg-[#FFF8ED] text-[#C98A2E]',
    description: 'This listing hasn’t been confirmed recently or its accuracy dropped. Check with the store before you go — it’s not covered by the guarantee.'
  },
  Earning: {
    label: 'Earning',
    Icon: CircleDashed,
    pill: 'bg-gray-100 text-[#60646F]',
    description: 'A new or low-volume item still building its accuracy track record from real pickups. It hasn’t earned the Stashed badge yet.'
  },
  'At risk': {
    label: 'At risk',
    Icon: AlertCircle,
    pill: 'bg-[#FFF8ED] text-[#C98A2E]',
    description: 'Recent pickups flagged a problem with this item. Its accuracy is slipping and the Stashed badge may drop soon.'
  }
};
export function StatusPill({
  status
}: {
  status: Product['status'];
}) {
  const {
    label,
    Icon,
    pill,
    description
  } = STATUS_CONFIG[status];
  return <span className="relative inline-flex group/status" data-id="element-333">
      <button type="button" aria-label={`${label}: ${description}`} onClick={e => e.stopPropagation()} className={clsx('inline-flex items-center gap-1 px-2 py-0.5 rounded-sm text-[10px] font-bold uppercase tracking-wider cursor-help focus:outline-none focus:ring-2 focus:ring-[#00CDBE]', pill)} data-id="element-334">
        <Icon className="w-3 h-3" data-id="element-335" />
        {label}
      </button>
      <span role="tooltip" className="pointer-events-none absolute bottom-full left-0 mb-2 w-56 rounded-lg bg-[#252935] p-3 text-[11px] font-normal normal-case leading-snug tracking-normal text-white opacity-0 shadow-lg transition-opacity duration-150 group-hover/status:opacity-100 group-focus-within/status:opacity-100 z-40" data-id="element-336">
        <span className="block font-bold mb-1" data-id="element-337">{label}</span>
        {description}
      </span>
    </span>;
}
type SortKey = 'featured' | 'price-asc' | 'price-desc' | 'rating' | 'distance';
const CATEGORIES = ['All categories', 'Flower', 'Edibles', 'Vapes', 'Pre-rolls', 'Tinctures'];
const SORT_OPTIONS: {
  key: SortKey;
  label: string;
}[] = [{
  key: 'featured',
  label: 'Featured'
}, {
  key: 'price-asc',
  label: 'Price: Low to High'
}, {
  key: 'price-desc',
  label: 'Price: High to Low'
}, {
  key: 'rating',
  label: 'Top rated'
}, {
  key: 'distance',
  label: 'Nearest'
}];
// Featured ranking: Stashed first, then Earning/At risk, Not verified last.
const STATUS_RANK: Record<Product['status'], number> = {
  Stashed: 0,
  Earning: 1,
  'At risk': 1,
  'Not verified': 2
};
function ProductCard({
  product,
  onClick,
  ad = false,
  isSeller = false
}: {
  product: Product;
  onClick: () => void;
  ad?: boolean;
  isSeller?: boolean;
}) {
  return <div onClick={onClick} className="flex flex-col cursor-pointer group" data-id="element-338">
      <div className="relative aspect-square bg-[#F4F5F6] rounded-2xl overflow-hidden mb-3" data-id="element-339">
        <img src={product.image} alt={product.name} onError={e => {
        e.currentTarget.src = 'https://images.unsplash.com/photo-1620912189865-1e8a32b1d6a2?auto=format&fit=crop&q=80&w=300&h=300';
      }} className="w-full h-full object-cover" data-id="element-340" />
        <button className="absolute top-3 right-3" onClick={e => e.stopPropagation()} data-id="element-341">
          <Heart className="w-6 h-6 text-white drop-shadow-md" data-id="element-342" />
        </button>
        {ad && <div className="absolute top-3 left-3 bg-[#252935]/80 text-white text-[10px] font-bold px-1.5 py-0.5 rounded" data-id="element-343">
            Ad
          </div>}
        <div className="absolute bottom-0 left-0 bg-[#D93A4B] text-white text-xs font-bold px-2 py-1" data-id="element-344">
          -{Math.round((1 - product.price / product.originalPrice) * 100)}%
        </div>
      </div>

      <div className="text-[11px] text-[#60646F] uppercase tracking-wider font-bold mb-1" data-id="element-345">
        {product.brand}
      </div>
      <div className="font-bold text-[#252935] text-base leading-tight mb-1" data-id="element-346">
        {product.name}
      </div>
      <div className="text-sm text-[#60646F] mb-1 flex items-center gap-1 flex-wrap" data-id="element-347">
        {product.dispensaryName}
        {isSeller && <span className="inline-flex items-center gap-0.5 text-[10px] font-bold text-[#007E7A] bg-[#E9FAF8] px-1.5 py-0.5 rounded-sm uppercase tracking-wider" data-id="element-348">
            <ShieldCheck className="w-3 h-3" data-id="element-349" />
            Stash Seller
          </span>}
      </div>

      <div className="flex items-center gap-1 text-sm text-[#252935] mb-1" data-id="element-350">
        <Star className="w-4 h-4 fill-current" data-id="element-351" />
        <span data-id="element-352">
          {product.rating} ({product.reviewCount})
        </span>
      </div>

      <div className="flex items-center gap-1.5 mb-2" data-id="element-353">
        <span className="text-[#D93A4B] font-bold text-base" data-id="element-354">
          ${product.price.toFixed(2)}
        </span>
        <span className="text-sm text-[#60646F] line-through" data-id="element-355">
          ${product.originalPrice.toFixed(2)}
        </span>
        <span className="text-sm text-[#60646F]" data-id="element-356">{product.size}</span>
      </div>

      <div className="mt-auto pt-2" data-id="element-357">
        {product.status === 'Stashed' && <p className="text-[10px] text-[#007E7A] mb-1" data-id="element-358">
            Checked {product.lastUpdated}
          </p>}
        <StatusPill status={product.status} data-id="element-359" />
      </div>
    </div>;
}
export function Browse() {
  const {
    products,
    setShopperView,
    dispensaries
  } = useStore();
  const [stashedOnly, setStashedOnly] = useState(false);
  const [orderOnline, setOrderOnline] = useState(false);
  const [category, setCategory] = useState('All categories');
  const [sortKey, setSortKey] = useState<SortKey>('featured');
  const [openMenu, setOpenMenu] = useState<'sort' | 'category' | null>(null);
  const matches = products.filter(p => stashedOnly ? p.status === 'Stashed' : true).filter(p => orderOnline ? p.inStock : true).filter(p => category === 'All categories' ? true : p.category === category);
  // Seller rank: products from a Stash Seller dispensary win ties within the
  // same status (e.g. a Stash Seller's Stashed item outranks a non-seller's).
  const sellerRank = (p: Product) => isStashSeller(products, dispensaries, p.dispensaryName) ? 0 : 1;
  // Featured trust comparator: status first, then Stash Seller status.
  const trustCompare = (a: Product, b: Product) => STATUS_RANK[a.status] - STATUS_RANK[b.status] || sellerRank(a) - sellerRank(b);
  const applySort = (a: Product, b: Product) => {
    switch (sortKey) {
      case 'price-asc':
        return a.price - b.price;
      case 'price-desc':
        return b.price - a.price;
      case 'rating':
        return b.rating - a.rating;
      case 'distance':
        return parseFloat(a.distance) - parseFloat(b.distance);
      default:
        // Featured: rank organic results by Stash trust signal
        return trustCompare(a, b);
    }
  };
  // Top 2 slots are sponsored ads. Ads still follow the Stash trust
  // hierarchy — Stashed always ranks ahead of Earning/At risk, then Not
  // verified, with Stash Seller items winning ties.
  const sponsoredProducts = matches.filter(p => p.sponsored).sort(trustCompare).slice(0, 2);
  const sponsoredIds = new Set(sponsoredProducts.map(p => p.id));
  const organicProducts = matches.filter(p => !sponsoredIds.has(p.id)).sort(applySort);
  const filteredProducts = [...sponsoredProducts, ...organicProducts];
  const sortLabel = SORT_OPTIONS.find(o => o.key === sortKey)?.label ?? 'Sort';
  return <div className="flex flex-col h-full bg-white" data-id="element-360">
      {/* Header */}
      <div className="sticky top-0 bg-white z-10" data-id="element-361">
        <div className="flex items-center justify-between px-4 py-3" data-id="element-362">
          <div className="w-20" data-id="element-363"></div> {/* Spacer to center logo */}
          <div className="flex-1 flex justify-center" data-id="element-364">
            <WmLogo className="h-9 w-auto" data-id="element-365" />
          </div>
          <div className="flex items-center gap-3 text-[#252935] w-auto justify-end" data-id="element-366">
            <button onClick={() => setShopperView('PostPickup')} className="relative" data-id="element-367">
              <Bell className="w-6 h-6" data-id="element-368" />
              <span className="absolute top-0 right-0 w-2.5 h-2.5 bg-[#D93A4B] rounded-full border-2 border-white" data-id="element-369"></span>
            </button>
            <Heart className="w-6 h-6" data-id="element-370" />
            <User className="w-6 h-6" data-id="element-371" />
            <ShoppingCart className="w-6 h-6" data-id="element-372" />
          </div>
        </div>

        {/* Search Bar */}
        <div className="px-4 py-1" data-id="element-373">
          <div className="flex items-center bg-[#F4F5F6] rounded-lg p-2.5" data-id="element-374">
            <Search className="w-5 h-5 text-[#60646F] mr-2" data-id="element-375" />
            <input type="text" placeholder="Flower" className="bg-transparent flex-1 outline-none text-[#252935] text-base" data-id="element-376" />
            <span className="text-[#0066CC] text-base" data-id="element-377">Seattle, WA</span>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-[rgba(37,41,53,0.12)] mt-2" data-id="element-378">
          <button className="flex-1 py-3 text-center text-[#60646F] text-lg" data-id="element-379">
            All
          </button>
          <button className="flex-1 py-3 text-center text-[#252935] font-bold border-b-[3px] border-[#00CDBE] text-lg" data-id="element-380">
            Products
          </button>
        </div>

        {/* Filters */}
        <div className="relative" data-id="element-381">
          <div className="flex gap-2 overflow-x-auto px-4 py-3 scrollbar-hide" data-id="element-382">
            <button onClick={() => setOpenMenu(openMenu === 'sort' ? null : 'sort')} className={clsx('flex items-center gap-1 px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors', sortKey !== 'featured' ? 'bg-[#252935] text-white' : 'bg-[#F4F5F6] text-[#252935]')} data-id="element-383">
              {sortKey === 'featured' ? 'Sort' : sortLabel}
              <ChevronDown className="w-4 h-4" data-id="element-384" />
            </button>

            <button onClick={() => setOpenMenu(openMenu === 'category' ? null : 'category')} className={clsx('flex items-center gap-1 px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors', category !== 'All categories' ? 'bg-[#252935] text-white' : 'bg-[#F4F5F6] text-[#252935]')} data-id="element-385">
              {category === 'All categories' ? 'Category' : category}
              <ChevronDown className="w-4 h-4" data-id="element-386" />
            </button>

            <button onClick={() => setOrderOnline(!orderOnline)} className={clsx('flex items-center gap-1 px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors', orderOnline ? 'bg-[#252935] text-white' : 'bg-[#F4F5F6] text-[#252935]')} data-id="element-387">
              Order online
            </button>

            <button onClick={() => setStashedOnly(!stashedOnly)} className={clsx('flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap border transition-colors', stashedOnly ? 'bg-[#252935] text-white border-[#252935]' : 'bg-[#F4F5F6] text-[#252935] border-transparent')} data-id="element-388">
              <Tag className="w-4 h-4" data-id="element-389" /> Stashed
            </button>
          </div>

          {openMenu === 'sort' && <div className="absolute left-4 top-full -mt-1 w-56 bg-white rounded-xl shadow-lg border border-[rgba(37,41,53,0.12)] py-1 z-30" data-id="element-390">
              {SORT_OPTIONS.map(opt => <button key={opt.key} onClick={() => {
            setSortKey(opt.key);
            setOpenMenu(null);
          }} className={clsx('w-full text-left px-4 py-2.5 text-sm hover:bg-gray-50', sortKey === opt.key ? 'text-[#007E7A] font-bold' : 'text-[#252935]')} data-id="element-391">
                  {opt.label}
                </button>)}
            </div>}

          {openMenu === 'category' && <div className="absolute left-24 top-full -mt-1 w-48 bg-white rounded-xl shadow-lg border border-[rgba(37,41,53,0.12)] py-1 z-30" data-id="element-392">
              {CATEGORIES.map(cat => <button key={cat} onClick={() => {
            setCategory(cat);
            setOpenMenu(null);
          }} className={clsx('w-full text-left px-4 py-2.5 text-sm hover:bg-gray-50', category === cat ? 'text-[#007E7A] font-bold' : 'text-[#252935]')} data-id="element-393">
                  {cat}
                </button>)}
            </div>}
        </div>
      </div>

      {/* Promo banner ad unit */}
      <div className="px-4 pt-4" data-id="element-394">
        <div className="relative overflow-hidden rounded-2xl bg-[#5CE0D8] p-5" data-id="element-395">
          <span className="absolute top-3 right-3 text-[11px] font-medium text-[#252935]/70" data-id="element-396">
            Ad
          </span>
          <p className="text-sm font-medium text-[#252935]/80 mb-0.5" data-id="element-397">
            Skip the line, order online
          </p>
          <h3 className="text-xl font-extrabold text-[#252935] leading-tight mb-3 max-w-[70%]" data-id="element-398">
            Pick up your favorites nearby
          </h3>
          <button className="bg-[#252935] text-white text-sm font-bold px-5 py-2 rounded-full" data-id="element-399">
            Order now
          </button>
        </div>
      </div>

      {/* Empty state */}
      {filteredProducts.length === 0 && <div className="flex flex-col items-center justify-center text-center px-8 py-16 text-[#60646F]" data-id="element-400">
          <Search className="w-8 h-8 mb-3 opacity-40" data-id="element-401" />
          <p className="font-bold text-[#252935] mb-1" data-id="element-402">No products found</p>
          <p className="text-sm" data-id="element-403">Try clearing a filter to see more results.</p>
        </div>}

      {/* Sponsored ads — top of results */}
      {sponsoredProducts.length > 0 && <>
          <div className="px-4 pt-4 flex items-center gap-2" data-id="element-404">
            <h2 className="text-sm font-bold text-[#60646F] uppercase tracking-wider" data-id="element-405">
              Sponsored
            </h2>
          </div>
          <div className="grid grid-cols-2 gap-x-4 gap-y-8 px-4 pt-3" data-id="element-406">
            {sponsoredProducts.map(product => <ProductCard key={product.id} product={product} ad isSeller={isStashSeller(products, dispensaries, product.dispensaryName)} onClick={() => setShopperView('Detail', product.id)} data-id="element-407" />)}
          </div>
        </>}

      {/* Organic results — ranked by Stash trust signal */}
      {organicProducts.length > 0 && <>
          <div className="px-4 pt-6 flex items-baseline justify-between" data-id="element-408">
            <h2 className="text-sm font-bold text-[#60646F] uppercase tracking-wider" data-id="element-409">
              {organicProducts.length} results
            </h2>
            {sortKey === 'featured' && <span className="text-xs text-[#007E7A] font-medium" data-id="element-410">
                Stashed ranks first
              </span>}
          </div>
          <div className="grid grid-cols-2 gap-x-4 gap-y-8 p-4" data-id="element-411">
            {organicProducts.map(product => <ProductCard key={product.id} product={product} isSeller={isStashSeller(products, dispensaries, product.dispensaryName)} onClick={() => setShopperView('Detail', product.id)} data-id="element-412" />)}
          </div>
        </>}
    </div>;
}